class ImageContent extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToMotion) return;
    this.isConnectedToMotion = true;

    this.tiles = Array.from(this.querySelectorAll('[data-image-content-tile]'));
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.targetX = 0;
    this.targetY = 0;
    this.currentX = 0;
    this.currentY = 0;

    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerLeave = this.handlePointerLeave.bind(this);
    this.onViewportChange = this.requestRender.bind(this);
    this.onMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);

    this.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    this.prefersReducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.hasFinePointer.addEventListener?.('change', this.onMotionPreferenceChange);

    if ('IntersectionObserver' in window && !this.prefersReducedMotion.matches) {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-visible');
          this.revealObserver?.disconnect();
        },
        { threshold: 0.25 }
      );
      this.revealObserver.observe(this);
    } else {
      this.classList.add('is-visible');
    }

    this.requestRender();
  }

  disconnectedCallback() {
    this.isConnectedToMotion = false;
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    this.prefersReducedMotion?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.hasFinePointer?.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.revealObserver?.disconnect();
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
  }

  handlePointerMove(event) {
    if (!this.motionIsAllowed()) return;
    const bounds = this.getBoundingClientRect();
    this.targetX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    this.targetY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    this.requestRender();
  }

  handlePointerLeave() {
    this.targetX = 0;
    this.targetY = 0;
    this.requestRender();
  }

  handleMotionPreferenceChange() {
    if (!this.motionIsAllowed()) {
      this.targetX = 0;
      this.targetY = 0;
      this.currentX = 0;
      this.currentY = 0;
    }
    this.requestRender();
  }

  motionIsAllowed() {
    return !this.prefersReducedMotion.matches && this.hasFinePointer.matches && window.innerWidth >= 750;
  }

  requestRender() {
    if (this.frameRequest) return;
    this.frameRequest = requestAnimationFrame(() => {
      this.frameRequest = 0;
      this.render();
    });
  }

  render() {
    if (!this.tiles?.length) return;

    const bounds = this.getBoundingClientRect();
    const isVisible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    if (!isVisible) return;

    const canTrackPointer = this.motionIsAllowed();
    const ease = 0.075;
    this.currentX += ((canTrackPointer ? this.targetX : 0) - this.currentX) * ease;
    this.currentY += ((canTrackPointer ? this.targetY : 0) - this.currentY) * ease;

    const offsetFromViewportCenter = bounds.top + bounds.height / 2 - window.innerHeight / 2;

    this.tiles.forEach((tile) => {
      const strength = Number.parseFloat(tile.dataset.strength) || 0;
      const scrollFactor = Number.parseFloat(tile.dataset.scrollFactor) || 0;
      tile.style.setProperty('--image-content-x', `${(this.currentX * strength).toFixed(2)}px`);
      tile.style.setProperty('--image-content-y', `${(this.currentY * strength * 0.5).toFixed(2)}px`);
      tile.style.setProperty(
        '--image-content-scroll-y',
        `${(window.innerWidth >= 750 && !this.prefersReducedMotion.matches ? offsetFromViewportCenter * scrollFactor : 0).toFixed(2)}px`
      );
    });

    const stillEasing =
      Math.abs(this.currentX - (canTrackPointer ? this.targetX : 0)) > 0.002 ||
      Math.abs(this.currentY - (canTrackPointer ? this.targetY : 0)) > 0.002;
    if (stillEasing) this.requestRender();
  }
}

if (!customElements.get('image-content')) {
  customElements.define('image-content', ImageContent);
}
